import React from 'react';
import useStyles from './styles';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useRouter } from 'next/router';
import { ROUTES } from 'config/Nav';
import { IMAGE_404, IMAGE_500 } from 'config/Error';
import { Button, Logo, secondaryOutlinedButtonSx } from '@elevator/ui';

/**
 * Error Component
 *
 * Displays error pages with appropriate messages and background images based on status code.
 * Handles 404 (page not found) and other errors (500, etc.) with different messaging.
 *
 * Features:
 * - Status code-based error messages
 * - GIPHY background images for visual appeal
 * - Home navigation button
 * - Site logo (upper left; page renders without Appbar)
 * - GIPHY attribution link
 *
 * Used in:
 * - /404 page (404 errors)
 * - _error.tsx (general error handling)
 *
 * @param props - Component props
 * @param props.statusCode - HTTP status code (404, 500, etc.)
 * @returns Error component with appropriate error message and styling
 */
const Error = (props: ErrorPropTypes): React.JSX.Element => {
  const { statusCode = 404 } = props;
  const { classes } = useStyles();

  const backgroundImageUrl = statusCode === 404 ? IMAGE_404.src : IMAGE_500.src;

  const router = useRouter();

  const onGoHomeClick = () => {
    router.replace(ROUTES.HOME.to);
  };

  const getGIPHYAttribution = (): string => {
    if (statusCode === 404) {
      return IMAGE_404.href;
    }
    return IMAGE_500.href;
  };

  return (
    <Box
      className={classes.container}
      sx={{
        backgroundImage: `url("${backgroundImageUrl}")`,
      }}
    >
      <Box className={classes.logoContainer}>
        <Logo href="/" width={120} height={36} glitch />
      </Box>

      <Stack className={classes.content} spacing={4} alignItems="center">
        {statusCode === 404 ? (
          <Stack spacing={1} alignItems="center">
            <Typography align="center" variant="h4" className={classes.errorTitle}>
              404 page not found
            </Typography>
            <Typography align="center" variant="body1" className={classes.bodyText}>
              That page don&apos;t exist, Na!
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={1} alignItems="center">
            <Typography align="center" variant="h4" className={classes.errorTitle}>
              Oh snap! Something went wrong
            </Typography>
            <Typography align="center" variant="body1" className={classes.bodyText}>
              Your error has been reported to the ELEVATOR dev team
            </Typography>
          </Stack>
        )}
        <Button variant="outlined" sx={secondaryOutlinedButtonSx} onClick={onGoHomeClick}>
          Home
        </Button>
      </Stack>

      <a
        href={getGIPHYAttribution()}
        className={classes.giphyRef}
        target="_blank"
        rel="noreferrer"
      >
        via GIPHY
      </a>
    </Box>
  );
};

export type ErrorPropTypes = {
  statusCode?: number;
};

export default Error;
